// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-MAP-1 — TanStack Query hooks for the mapped-column backfill (E-02: components never call api.* directly).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as api from '../../api/client'

export const backfillKeys = {
  status: (pharmacyId: string | undefined) => ['mapping-backfill', pharmacyId ?? 'bound'] as const,
}

/** The schedule and the runs. Polls every 5 s while a run is active, never otherwise. */
export function useMappingBackfill(pharmacyId: string | undefined) {
  return useQuery({
    queryKey: backfillKeys.status(pharmacyId),
    queryFn: () => api.getMappingBackfill(pharmacyId),
    refetchInterval: q => (q.state.data?.active ? 5_000 : false),
    staleTime: 5_000,
  })
}

export function useStartMappingBackfill(pharmacyId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (trigger: 'manual' | 'save') => api.startMappingBackfill(pharmacyId, trigger),
    onSuccess: data => { qc.setQueryData(backfillKeys.status(pharmacyId), data) },
  })
}

export function useCancelMappingBackfill(pharmacyId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.cancelMappingBackfill(pharmacyId),
    onSuccess: data => { qc.setQueryData(backfillKeys.status(pharmacyId), data) },
  })
}

export function useSaveMappingBackfillSettings(pharmacyId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (s: Partial<api.MappingBackfillSettings>) => api.saveMappingBackfillSettings(pharmacyId, s),
    onSuccess: data => { qc.setQueryData(backfillKeys.status(pharmacyId), data) },
  })
}
